// 모바일 이메일 가입/로그인 — 순수 검증·변환 로직 (supabase 비의존)
// - 웹 app/auth/signup·login page와 정책 정합 (비번/닉네임/에러 한국어)
// - Supabase 호출(signUp/signInWithPassword)은 LoginPage가 직접 — 이 파일은 capacitor/supabase
//   import 체인을 끌고 오지 않아 vitest 순수 단위테스트가 깔끔 (tradeError.ts와 동일 패턴)

export interface PwStatus {
  length: boolean;
  letter: boolean;
  number: boolean;
  special: boolean;
}

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

// OAuth 콜백이 발급하는 가짜 이메일 도메인 — 일반 가입으로 사칭 차단 (웹 signup 가드 정합)
const PLACEHOLDER_DOMAIN = /@noreply\.(choishin\.net|sotda\.com)$/i;
// 특수문자 집합 — 웹 signup validatePassword와 동일
const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/;

// 비밀번호 실시간 체크 상태 (UI 체크리스트용) — 웹 signup과 동일 규칙
export function passwordStatus(pw: string): PwStatus {
  return {
    length: pw.length >= 8,
    letter: /[a-zA-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: SPECIAL_RE.test(pw),
  };
}

// 비밀번호 정책: 8자 이상 + 영문(대소 무관) + 숫자 + 특수문자
export function validatePassword(pw: string): ValidationResult {
  if (pw.length < 8) return { valid: false, message: "비밀번호는 8자 이상이어야 합니다." };
  if (!/[a-zA-Z]/.test(pw)) return { valid: false, message: "비밀번호에 영문을 포함해야 합니다." };
  if (!/[0-9]/.test(pw)) return { valid: false, message: "비밀번호에 숫자를 포함해야 합니다." };
  if (!SPECIAL_RE.test(pw)) return { valid: false, message: "비밀번호에 특수문자(!@#$% 등)를 포함해야 합니다." };
  return { valid: true };
}

// 닉네임: 2~20자, 한글/영문/숫자/_ 허용 (웹 signup 정합)
export function validateNickname(nick: string): ValidationResult {
  if (nick.length < 2 || nick.length > 20) return { valid: false, message: "닉네임은 2~20자여야 합니다." };
  if (!/^[a-zA-Z0-9가-힣_]+$/.test(nick)) {
    return { valid: false, message: "닉네임은 한글/영문/숫자/_만 사용 가능합니다." };
  }
  return { valid: true };
}

// 이메일 형식 + placeholder 도메인 거부
export function validateEmail(email: string): ValidationResult {
  const e = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return { valid: false, message: "올바른 이메일 형식이 아닙니다." };
  if (PLACEHOLDER_DOMAIN.test(e)) return { valid: false, message: "해당 이메일 도메인은 사용할 수 없습니다." };
  return { valid: true };
}

/** 어떤 동작 중에 난 오류인가. 같은 500이라도 가입과 로그인은 원인·안내가 다르다 (N-42). */
export type AuthContext = "signup" | "login";

// Supabase auth 에러 → 한국어 (웹 translateSignupError + translateAuthError 통합 포팅)
//
// ⚠ N-42 (2026-07-30): 가입과 로그인이 같은 번역 함수를 쓰면서 `status === 500`만으로
//   **"확인 메일 발송에 실패했다"** 분기에 들어갔다. 로그인은 확인 메일을 보내지 않으므로,
//   기존 사용자가 Auth·DB 장애로 500을 받으면 실제 장애와 무관한 원인·대응을 안내받았다.
//   → 동작 문맥을 받아 그 카피는 **가입에서만** 쓴다. context 미지정은 기존 동작을 유지한다.
export function translateAuthError(
  error: { message?: string; code?: string; status?: number } | null,
  context: AuthContext = "signup",
): string {
  const msg = (error?.message || "").toLowerCase();
  const code = error?.code || "";
  if (msg.includes("invalid login credentials") || code === "invalid_credentials") {
    return "이메일 또는 비밀번호가 올바르지 않습니다.";
  }
  if (msg.includes("email not confirmed") || code === "email_not_confirmed") {
    return "이메일 인증이 완료되지 않았습니다. 받은 메일의 인증 링크를 먼저 눌러주세요.";
  }
  if (
    msg.includes("user already registered") ||
    msg.includes("already registered") ||
    code === "user_already_exists"
  ) {
    return "이미 가입된 이메일입니다.";
  }
  if (msg.includes("weak password") || code === "weak_password" || msg.includes("password should")) {
    return "비밀번호는 8자 이상, 영문·숫자·특수문자를 모두 포함해야 합니다.";
  }
  if (msg.includes("invalid email") || code === "invalid_email") {
    return "올바른 이메일 형식이 아닙니다.";
  }
  // 확인 메일 발송 실패 (SMTP/Resend 다운) — Supabase가 500 unexpected_failure 반환, user는 롤백됨
  //   메시지가 메일 발송을 직접 가리키면 문맥과 무관하게 이 안내가 맞다.
  const mailFailureMessage = msg.includes("error sending") || msg.includes("confirmation email");
  if (mailFailureMessage) {
    return "확인 메일 발송에 일시적으로 실패했어요. 잠시 후 다시 시도하거나, 소셜 로그인(구글·네이버·카카오)을 이용해 주세요.";
  }
  // 코드·상태만으로 판정하는 건 **가입일 때만** — 로그인 500은 메일과 무관하다(N-42).
  if (code === "unexpected_failure" || error?.status === 500) {
    if (context === "signup") {
      return "확인 메일 발송에 일시적으로 실패했어요. 잠시 후 다시 시도하거나, 소셜 로그인(구글·네이버·카카오)을 이용해 주세요.";
    }
    return "로그인 서비스에 일시적인 문제가 있어요. 잠시 후 다시 시도하거나, 소셜 로그인(구글·네이버·카카오)을 이용해 주세요.";
  }
  if (msg.includes("signup is disabled") || code === "signup_disabled") {
    return "현재 회원가입이 비활성화되어 있습니다.";
  }
  if (
    msg.includes("email rate limit") ||
    code === "over_email_send_rate_limit" ||
    msg.includes("too many requests") ||
    error?.status === 429
  ) {
    return "짧은 시간에 너무 많이 시도했습니다. 잠시 후 다시 시도해주세요.";
  }
  if (msg.includes("network") || msg.includes("failed to fetch")) {
    return "네트워크 연결을 확인해주세요.";
  }
  return "요청에 실패했습니다. 입력 정보를 확인해주세요.";
}

// 가입 폼 전체 검증 (이메일·비번·닉네임 순차) — 첫 실패 메시지 반환
export function validateSignupForm(p: { email: string; password: string; nickname: string }): ValidationResult {
  const e = validateEmail(p.email);
  if (!e.valid) return e;
  const pw = validatePassword(p.password);
  if (!pw.valid) return pw;
  const n = validateNickname(p.nickname);
  if (!n.valid) return n;
  return { valid: true };
}
